import React, { useEffect, useState } from 'react';
import {
  FiArrowLeft, FiChevronLeft, FiChevronRight, FiDownload, FiFileText, FiImage, FiInfo, FiMinus, FiPlus, FiRotateCw,
} from 'react-icons/fi';

// Field names on procurement API rows aren't documented, so read each value from the
// first key that exists on the row.
export const pick = (row, keys, fallback = '') => {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
  }
  return fallback;
};

export const PROCUREMENT_HOST = (process.env.REACT_APP_PROCUREMENT_API_URL || 'https://proc.kdapi.apps.rabahrain.com').replace(/\/+$/, '');

// File rows aren't documented either; resolve a name and a link from likely keys.
export const normalizeFile = (f, i) => {
  if (typeof f === 'string') f = { filePath: f };
  const link = pick(f, ['fileUrl', 'url', 'filePath', 'path', 'link', 'fileLink'], '');
  const name = pick(f, ['fileName', 'name', 'docFileName', 'description'], String(link).split('/').pop() || `File ${i + 1}`);
  // Files are served from <host>/Resources/<fileName>; use the file name when the row has no explicit link.
  const target = String(link || name || '').replace(/^\/+/, '');
  const href = !target ? ''
    : /^(https?:|data:)/i.test(target) ? target
      : `${PROCUREMENT_HOST}/${target.includes('/') ? target : `Resources/${encodeURIComponent(target)}`}`;
  return {
    key: `${name}-${i}`,
    name,
    href,
    remarks: pick(f, ['remarks', 'remark', 'fileRemarks'], ''),
    size: pick(f, ['fileSize', 'size', 'length'], ''),
    uploadedOn: pick(f, ['uploadedOn', 'uploadedDate', 'createdOn', 'createdDate', 'date'], ''),
    uploadedBy: pick(f, ['uploadedBy', 'createdBy', 'userName'], ''),
  };
};

export const IMAGE_RE = /\.(jpe?g|png|gif|bmp|webp|svg)(\?.*)?$/i;
export const fileExt = (name) => (String(name).split('.').pop() || '').toUpperCase().slice(0, 4);
export const formatSize = (v) => {
  const n = Number(v);
  if (!n) return '—';
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};
const KIND_LABEL = { JPG: 'JPEG image', JPEG: 'JPEG image', PNG: 'PNG image', GIF: 'GIF image', PDF: 'PDF document' };

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];

// Pagination exactly as the design: square 34px buttons, accent-filled current page.
export const ProcPager = ({ pagination, onPage }) => {
  const current = pagination?.currentPage || 1;
  const totalPages = pagination?.totalPages || 1;
  const pages = [];
  const start = Math.max(1, Math.min(current - 2, totalPages - 4));
  for (let p = start; p <= Math.min(totalPages, start + 4); p += 1) pages.push(p);
  return (
    <div className="proc-pager">
      <button type="button" className="proc-pagebtn" aria-label="Previous page" disabled={current <= 1} onClick={() => onPage(current - 1)}>
        <FiChevronLeft size={16} />
      </button>
      {pages.map((p) => (p === current
        ? <span key={p} aria-current="page" className="proc-page-current">{p}</span>
        : <button key={p} type="button" className="proc-pagebtn" onClick={() => onPage(p)}>{p}</button>))}
      <button type="button" className="proc-pagebtn" aria-label="Next page" disabled={current >= totalPages} onClick={() => onPage(current + 1)}>
        <FiChevronRight size={16} />
      </button>
    </div>
  );
};

// Full-screen viewer: files rail, preview with zoom/rotate, details panel.
export const FilePreview = ({ files, startKey, onClose, doc = {} }) => {
  const [index, setIndex] = useState(Math.max(0, files.findIndex((f) => f.key === startKey)));
  const [zi, setZi] = useState(2);
  const [rot, setRot] = useState(0);
  const [infoOn, setInfoOn] = useState(true);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [dims, setDims] = useState('');
  const file = files[index];
  const isImage = IMAGE_RE.test(file?.href || '');

  const select = (i) => {
    setIndex(i);
    setZi(2);
    setRot(0);
    setStatus('loading');
    setDims('');
  };
  const step = (d) => files.length > 1 && select((index + d + files.length) % files.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === '+' || e.key === '=') setZi((z) => Math.min(ZOOM_STEPS.length - 1, z + 1));
      else if (e.key === '-') setZi((z) => Math.max(0, z - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, files.length]);

  if (!file) return null;
  const ext = fileExt(file.name);

  return (
    <div className="pv" role="dialog" aria-modal="true" aria-label={`Viewing ${file.name}`}>
      <header className="pv-head">
        <button type="button" className="pv-ghost" onClick={onClose}><FiArrowLeft size={16} />Attachments</button>
        <div className="pv-title">
          <div className="pv-name" title={file.name}>{file.name}</div>
          <div className="pv-meta">
            <span className="pv-num">{doc.docNo}</span><span aria-hidden="true">·</span>
            <span>{doc.docType}</span><span aria-hidden="true">·</span>
            <span className="pv-num">{index + 1} of {files.length}</span>
          </div>
        </div>
        <div className="pv-tools">
          {isImage && (
            <div role="group" aria-label="Zoom" className="pv-zoom">
              <button type="button" className="pv-tool" aria-label="Zoom out" onClick={() => setZi((z) => Math.max(0, z - 1))}><FiMinus size={16} /></button>
              <span className="pv-num pv-zoom-label">{Math.round(ZOOM_STEPS[zi] * 100)}%</span>
              <button type="button" className="pv-tool" aria-label="Zoom in" onClick={() => setZi((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))}><FiPlus size={16} /></button>
              <button type="button" className={`pv-tool${zi === 2 && rot === 0 ? ' on' : ''}`} onClick={() => { setZi(2); setRot(0); }}>Fit</button>
            </div>
          )}
          {isImage && <span aria-hidden="true" className="pv-sep" />}
          {isImage && <button type="button" className="pv-tool" aria-label="Rotate" onClick={() => setRot((r) => (r + 90) % 360)}><FiRotateCw size={16} /></button>}
          <a className="pv-tool" aria-label="Download" href={file.href} download={file.name} target="_blank" rel="noreferrer"><FiDownload size={16} /></a>
          <button type="button" className={`pv-tool${infoOn ? ' on' : ''}`} aria-pressed={infoOn} onClick={() => setInfoOn((v) => !v)}><FiInfo size={16} />Details</button>
        </div>
      </header>

      <div className="pv-body">
        <aside className="pv-rail" aria-label="Files in this document">
          <div className="pv-label pv-rail-title">Files · {files.length}</div>
          {files.map((f, i) => {
            const fe = fileExt(f.name);
            const img = IMAGE_RE.test(f.href);
            return (
              <button type="button" key={f.key} className={`pv-thumb${i === index ? ' on' : ''}`} onClick={() => select(i)} aria-current={i === index ? 'page' : undefined}>
                <span className="pv-thumb-icon">{img ? <FiImage size={20} color="#B9A6EA" /> : <FiFileText size={20} color="#E7A09A" />}</span>
                <span className="pv-thumb-text">
                  <span className="pv-thumb-name">{f.name}</span>
                  <span className="pv-thumb-meta pv-num">{fe}{f.size ? ` · ${formatSize(f.size)}` : ''}</span>
                </span>
              </button>
            );
          })}
        </aside>

        <section className="pv-stage" aria-label="Preview">
          {status === 'loading' && isImage && <div className="pv-msg">Loading…</div>}
          {status === 'error' && <div className="pv-msg">Couldn’t load this file. Use the download button to open it directly.</div>}
          {isImage ? (
            <img
              key={file.href}
              src={file.href}
              alt={file.name}
              className="pv-img"
              style={{ transform: `scale(${ZOOM_STEPS[zi]}) rotate(${rot}deg)`, display: status === 'error' ? 'none' : 'block' }}
              onLoad={(e) => { setStatus('ready'); setDims(`${e.target.naturalWidth} × ${e.target.naturalHeight} px`); }}
              onError={() => setStatus('error')}
              draggable={false}
            />
          ) : (
            <iframe key={file.href} src={file.href} title={file.name} className="pv-frame" />
          )}
          <button type="button" className="pv-tool pv-nav left" aria-label="Previous file" disabled={index === 0} onClick={() => step(-1)}><FiChevronLeft size={18} /></button>
          <button type="button" className="pv-tool pv-nav right" aria-label="Next file" disabled={index === files.length - 1} onClick={() => step(1)}><FiChevronRight size={18} /></button>
        </section>

        {infoOn && (
          <aside className="pv-info" aria-label="File details">
            <div className="pv-label">File details</div>
            <div className="pv-kvs">
              <div className="pv-kv"><span className="pv-label">File name</span><span className="pv-value">{file.name}</span></div>
              <div className="pv-kv"><span className="pv-label">Type</span><span className="pv-value">{KIND_LABEL[ext] || ext}</span></div>
              {dims && <div className="pv-kv"><span className="pv-label">Dimensions</span><span className="pv-value pv-num">{dims}</span></div>}
              {file.size ? <div className="pv-kv"><span className="pv-label">Size</span><span className="pv-value pv-num">{formatSize(file.size)}</span></div> : null}
              {file.uploadedOn ? <div className="pv-kv"><span className="pv-label">Uploaded</span><span className="pv-value"><span className="pv-num">{file.uploadedOn}</span>{file.uploadedBy ? ` by ${file.uploadedBy}` : ''}</span></div> : null}
              <div className="pv-kv"><span className="pv-label">Remarks</span><span className="pv-value">{file.remarks || '—'}</span></div>
            </div>
            <div className="pv-rule" />
            <div className="pv-label">Document</div>
            <div className="pv-kvs">
              <div className="pv-kv"><span className="pv-label">Document no</span><span className="pv-value pv-num">{doc.docNo}</span></div>
              <div className="pv-kv"><span className="pv-label">Type</span><span className="pv-value">{doc.docType}</span></div>
              {doc.docDate ? <div className="pv-kv"><span className="pv-label">Date</span><span className="pv-value pv-num">{doc.docDate}</span></div> : null}
              <div className="pv-kv"><span className="pv-label">Amount</span><span className="pv-value pv-num">{(Number(doc.amount) || 0).toFixed(3)}</span></div>
              {doc.status ? <div className="pv-kv"><span className="pv-label">Status</span><span className="pv-value pv-status">{doc.status}</span></div> : null}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};
